import * as coreBi from '#packages/coreBi';
import type { EditorAPI } from '#packages/editorAPI';
import * as stateManagement from '#packages/stateManagement';
import { array, media } from '#packages/util';
import _ from 'lodash';
import * as categoryDefinitionValidator from './categoryDefinitionValidator';
import imagesMap from './imagesMap';
import * as sectionDefinitionValidator from './sectionDefinitionValidator';
import sectionTypesMap from './sectionTypesMap';
import {
  ImageSources,
  type PlatformAppCategoryDefinition,
  type PlatformAppItemDefinition,
  type PlatformAppWithCategoryDefinition,
} from './types';
import { shouldShowInAddPanel } from './appFilters';

import type {
  DSRead,
  CompRef,
  PlatformAppDescription,
  AppData,
  CompStructure,
} from 'types/documentServices';
import type { Point } from '#packages/rEditor';
import {
  type Group,
  type Section,
  EditorPlatformHostIntegrationAPI,
} from '@wix/editor-platform-host-integration-apis';
import experiment from 'experiment';
import { type DraggableItem } from '@/addPanelInfra';
import addPanelDataConsts from '#packages/addPanelDataConsts';

const platformSelectors = stateManagement.platform.selectors;
const platformActions = stateManagement.platform.actions;
const SYMBOL = 'plusBig';
const TOOLTIP = 'add_section_add_page_blankpage_tooltip';
const CATEGORY_TYPE = 'category';
const SECTION_TYPE = 'section';
const SUB_NAV_MIN_LENGTH = 5;
const DEFAULT_IMAGE_COUNT = 5;

const createImageUrlFromMap = (imageName: string) => {
  return media.getMediaUrl(imagesMap[imageName]);
};

const getDefaultImage = (categoryIndex: number): string =>
  createImageUrlFromMap(`LOGO${categoryIndex % DEFAULT_IMAGE_COUNT}`);

const setDefaultImageForItem = (subCategory: AnyFixMe, index: number) =>
  _.defaultsDeep(
    {
      props: {
        items: subCategory.props.items.map((item: AnyFixMe) =>
          _.defaults({}, item, {
            image: getDefaultImage(index),
          }),
        ),
      },
    },
    subCategory,
  );

const mapDefinitionToSection = (
  editorAPI: EditorAPI,
  app: PlatformAppDescription,
  {
    title,
    desc,
    image,
    imageSource,
    id,
    presetId,
    appExtraData,
    size,
    widgetId,
  }: PlatformAppItemDefinition,
) => ({
  id: widgetId,
  presetId,
  title,
  desc,
  shouldTranslate: false,
  showSectionHeader: true,
  symbol: SYMBOL,
  tooltip: TOOLTIP,
  image:
    imageSource === ImageSources.IMAGE_LIST
      ? createImageUrlFromMap(image)
      : image,
  size,
  onItemDrop: (
    position: Point,
    _item: DraggableItem,
    containerRef: CompRef,
  ) => {
    editorAPI.panelManager.closeAllPanels();
    editorAPI.store.dispatch(
      platformActions.notifyAddWidget(app.applicationId, id, {
        ...appExtraData,
        containerRef,
        position,
        size,
      }),
    );
    editorAPI.bi.event(coreBi.events.platform.add_widget_to_page, {
      source_name: 'add_panel',
      widget_id: widgetId || id,
      app_id: app.appDefinitionId,
    });
  },
  onItemClick: () => {
    editorAPI.panelManager.closeAllPanels();
    editorAPI.store.dispatch(
      platformActions.notifyAddWidget(app.applicationId, id, {
        ...appExtraData,
        size,
      }),
    );
    editorAPI.bi.event(coreBi.events.platform.add_widget_to_page, {
      source_name: 'add_panel',
      widget_id: widgetId || id,
      app_id: app.appDefinitionId,
    });
  },
});

const mapPlatformDefinitionToItems = (
  editorAPI: EditorAPI,
  section: Section,
  subCategoryId: string,
) =>
  section.items.map((item) => ({
    id: section.id,
    presetId: item.id,
    title: section.title,
    desc: item.title,
    shouldTranslate: false,
    showSectionHeader: true,
    symbol: SYMBOL,
    tooltip: TOOLTIP,
    image: item.image,
    size: item.size,
    ...(section.tooltipContent
      ? { tooltipContent: section.tooltipContent }
      : {}),
    onItemDrop: (
      position: Point,
      _item: DraggableItem,
      containerRef: CompRef,
    ) => {
      editorAPI.panelManager.closeAllPanels();
      item.addToStage(containerRef, position);
      editorAPI.bi.event(coreBi.events.platform.add_widget_to_page, {
        source_name: 'add_panel',
        widget_id: item.id,
        app_id: subCategoryId,
      });
    },
    onItemClick: () => {
      editorAPI.panelManager.closeAllPanels();
      const containerRef =
        editorAPI.addPanelInfra.addPanelUtils.getContainerToAddTo(
          editorAPI,
          {
            layout: item.size,
          } as CompStructure,
          undefined,
        );
      item.addToStage(containerRef);
      editorAPI.bi.event(coreBi.events.platform.add_widget_to_page, {
        source_name: 'add_panel',
        widget_id: item.id,
        app_id: subCategoryId,
      });
    },
  }));

const mapDefinitionToItems = (
  editorAPI: EditorAPI,
  app: PlatformAppDescription,
  { items }: PlatformAppCategoryDefinition,
) => {
  return _(items)
    .partition(sectionDefinitionValidator.validate)
    .thru(([validDefinitions, invalidDefinitions]) => {
      reportInvalidDefinitions(invalidDefinitions, SECTION_TYPE);
      return validDefinitions;
    })
    .map(_.partial(mapDefinitionToSection, editorAPI, app))
    .value();
};

const mapPlatformDefinitionToSubCategory = (
  editorAPI: EditorAPI,
  group: Group,
) => ({
  hide: false,
  id: group.id,
  type: addPanelDataConsts.SECTIONS_TYPES.GENERIC_LIST_SECTION,
  title: group.title,
  subNavigationTitle: group.title,
  presetTitle: group.title,
  subNavigationHide: !showSubNavigation(group.sections),
  showSectionHeader: true,
  shouldTranslate: false,
  disableInfoIcon: true,
  props: {
    items: group.sections.reduce(
      (items, section) =>
        items.concat(
          mapPlatformDefinitionToItems(editorAPI, section, group.id),
        ),
      [],
    ),
  },
});

const mapDefinitionToSubCategory = (
  editorAPI: EditorAPI,
  subNavigationHide: boolean,
  { app, categoryDefinition }: PlatformAppWithCategoryDefinition,
) => ({
  hide: false,
  id: app.appDefinitionId,
  type: sectionTypesMap[categoryDefinition.type],
  title: categoryDefinition.title,
  subNavigationTitle: categoryDefinition.title,
  presetTitle: categoryDefinition.header,
  subNavigationHide,
  showSectionHeader: true,
  shouldTranslate: false,
  disableInfoIcon: true,
  props: {
    items: mapDefinitionToItems(editorAPI, app, categoryDefinition),
  },
});

const showSubNavigation = (sections: AnyFixMe) =>
  sections.length >= SUB_NAV_MIN_LENGTH;

const addCategoryDefinition = (
  app: PlatformAppDescription,
  dsRead: DSRead,
): PlatformAppWithCategoryDefinition => ({
  app,
  categoryDefinition:
    platformSelectors.getAddPanelCategoryDefinitionFromManifest(
      dsRead,
      app.appDefinitionId,
    ),
});

const addCategoryDefinitionsToApps = (
  apps: PlatformAppDescription[],
  dsRead: DSRead,
): PlatformAppWithCategoryDefinition[] =>
  apps.map((app) => addCategoryDefinition(app, dsRead));

function partitionAppsByValidity(apps: PlatformAppWithCategoryDefinition[]) {
  return array.partition<PlatformAppWithCategoryDefinition>(apps, (app) =>
    categoryDefinitionValidator.validate(app.categoryDefinition),
  );
}

const createSectionsFromValidApps = (
  validDefinitions: PlatformAppWithCategoryDefinition[],
  editorAPI: EditorAPI,
) => {
  const subNavigationHide = !showSubNavigation(validDefinitions);

  const subCategories = validDefinitions.map((item) =>
    mapDefinitionToSubCategory(editorAPI, subNavigationHide, item),
  );

  return array.sortByProp(subCategories, 'title').map(setDefaultImageForItem);
};

// "Add" panel V2 currently used in Classic Editor
function createMyWidgetsSectionsAddPanelV2(editorAPI: EditorAPI) {
  const apps = stateManagement.platform.selectors
    .getInstalledBlocksApps(editorAPI.dsRead)
    .filter((app) => shouldShowInAddPanel(app as AppData));

  const appsWithCategoryDefinition = addCategoryDefinitionsToApps(
    apps,
    editorAPI.dsRead,
  );

  const [validApps] = partitionAppsByValidity(
    platformSelectors.filterPlatformAppPlugins(appsWithCategoryDefinition),
  );

  return createSectionsFromValidApps(validApps, editorAPI);
}

// "Add" panel V1 is deprecated and isn't used in Classic Editor anymore
function createMyWidgetsSectionsAddPanelV1(editorAPI: EditorAPI) {
  const apps = editorAPI.dsRead.platform.getInstalledEditorApps();

  const appsWithCategoryDefinition = addCategoryDefinitionsToApps(
    apps,
    editorAPI.dsRead,
  );

  const [validDefinitions, invalidDefinitions] = partitionAppsByValidity(
    appsWithCategoryDefinition,
  );

  reportInvalidDefinitions(invalidDefinitions, CATEGORY_TYPE);

  const sections = createSectionsFromValidApps(validDefinitions, editorAPI);

  return [
    {
      sections,
    },
  ];
}

const reportInvalidDefinitions = (
  definitions: AnyFixMe,
  definitionType: AnyFixMe,
) =>
  definitions.forEach((definition: AnyFixMe) =>
    console.error(
      `Invalid ${definitionType} definition: ${JSON.stringify(definition)}`,
    ),
  );

const getPlatformDataForAddPanel = (editorAPI: EditorAPI) => {
  const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
  const myWidgetsSections = platformAPI.panels.addPanel.getGroups();
  return myWidgetsSections.map((group: Group) =>
    mapPlatformDefinitionToSubCategory(editorAPI, group),
  );
};

const getMyWidgetsSections = (editorAPI: EditorAPI) => {
  const usePlatformDataForAddPanel = experiment.isOpen(
    'se_usePlatformMyWidgets',
  );
  return usePlatformDataForAddPanel
    ? getPlatformDataForAddPanel(editorAPI)
    : createMyWidgetsSectionsAddPanelV2(editorAPI);
};

export { createMyWidgetsSectionsAddPanelV1, getMyWidgetsSections };
